import { useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { LEGAL_CONFIG } from '@/lib/config';
import { PageLayout } from '@/components/Layout/PageLayout';

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <PageLayout title="Privacy Policy" maxWidth="2xl">
      <div className="space-y-6">
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-semibold">Your Data is Safe</h2>
          </div>

          <div className="space-y-4 text-base leading-relaxed text-foreground/80">
            <div className="space-y-2 pb-4 border-b">
              <h3 className="font-semibold text-foreground">Data Controller</h3>
              <p>
                {LEGAL_CONFIG.controllerName}<br />
                Contact: <a href={`mailto:${LEGAL_CONFIG.controllerEmail}`} className="text-primary underline">
                  {LEGAL_CONFIG.controllerEmail}
                </a><br />
                <Button
                  variant="link"
                  className="h-auto p-0 text-primary text-sm"
                  onClick={() => navigate('/imprint')}
                >
                  View full imprint →
                </Button>
              </p>
            </div>

            <p>
              Wren respects your privacy and is committed to protecting your personal information.
            </p>

            <div className="space-y-2">
              <h3 className="font-semibold text-foreground">What We Store</h3>
              <p>
                We store your expenses, chat entries, and progress data (XP, streaks, badges) securely 
                in our database. This data is necessary to provide you with expense tracking and gamification features.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-foreground">What We Don't Do</h3>
              <p>
                We do not sell or share your personal data with third parties. Your expense data remains 
                private and is only visible to you.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-foreground">Your Control</h3>
              <p>
                You can delete your data anytime from the Settings page by signing out and deleting your account. 
                All associated data will be permanently removed.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-foreground">Security</h3>
              <p>
                Your data is stored securely using industry-standard encryption and security practices. 
                We regularly update our security measures to protect your information.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-foreground">Error & Crash Reporting</h3>
              <p>
                When you opt in to crash reporting, we use <strong>Highlight.io</strong> to 
                collect error information to improve app stability. This includes:
              </p>
              <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>Error messages and stack traces (no personal data)</li>
                <li>Browser type and version</li>
                <li>Session recordings (with all text masked for privacy)</li>
              </ul>
              <p className="text-sm">
                <strong>Data residency:</strong> EU (Frankfurt)
              </p>
              <p className="text-sm">
                <strong>Opt-out:</strong> Go to{' '}
                <Button
                  variant="link"
                  className="h-auto p-0 text-primary text-sm inline"
                  onClick={() => navigate('/settings/privacy-analytics')}
                >
                  Privacy & Analytics Settings
                </Button>{' '}
                → disable "Crash Reporting"
              </p>
              <p className="text-sm">
                Read more:{' '}
                <a 
                  href="https://highlight.io/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="underline text-primary hover:text-primary/80"
                >
                  Highlight Privacy Policy
                </a>
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-foreground">Analytics & Measurement</h3>
              <p>
                We use <strong>Plausible Analytics</strong>, a privacy-friendly, cookieless analytics 
                service to understand how users interact with our app. This helps us improve performance 
                and fix issues.
              </p>
              <p className="text-sm">
                <strong>What we collect:</strong>
              </p>
              <ul className="text-sm space-y-1 ml-4">
                <li>• Anonymous usage events (e.g., "expense saved", "voice used")</li>
                <li>• General category information (no specific amounts or merchant names)</li>
                <li>• Page views and navigation patterns</li>
              </ul>
              <p className="text-sm">
                <strong>What we don't collect:</strong>
              </p>
              <ul className="text-sm space-y-1 ml-4">
                <li>• Personal expense details (amounts, merchants, notes)</li>
                <li>• IP addresses or device identifiers</li>
                <li>• Cross-site tracking data</li>
                <li>• Cookies or persistent identifiers</li>
              </ul>
              <p className="text-sm">
                <strong>Data residency:</strong> All analytics data is stored in the European Union 
                and processed in compliance with GDPR regulations. We do not sell or share analytics 
                data with third parties.
              </p>
              <p className="text-sm">
                <strong>Your control:</strong> Analytics is enabled by default (exempt from consent 
                requirements as it's cookieless and first-party). You can opt out anytime in{' '}
                <Button
                  variant="link"
                  className="h-auto p-0 text-primary text-sm"
                  onClick={() => navigate('/settings/privacy-analytics')}
                >
                  Privacy & Analytics Settings
                </Button>.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-foreground">Legal Basis (GDPR Article 6)</h3>
              <p>
                We process your personal data (expenses, progress, settings) based on your{' '}
                <strong>consent</strong> when you create an account. For analytics, we rely on your{' '}
                <strong>explicit consent</strong> (Article 6(1)(a)) provided via the consent banner 
                on first visit. We use cookieless, anonymized, EU-hosted measurement to minimize 
                privacy impact. You can change your choice anytime in Privacy & Analytics Settings.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-foreground">Data Retention</h3>
              <p>
                Your expense and profile data is stored as long as your account is active. 
                Analytics data is automatically deleted after <strong>12 months</strong> in 
                compliance with GDPR minimization principles. Account deletion requests are 
                processed within <strong>7 days</strong>. You can request deletion from Settings.
              </p>
            </div>

            <div className="space-y-2">
              <h3 className="font-semibold text-foreground">Your Rights</h3>
              <p>
                Under GDPR, you have the right to:
              </p>
              <ul className="text-sm space-y-1 ml-4">
                <li>• <strong>Access</strong> your personal data (view in app or export from Settings)</li>
                <li>• <strong>Rectification</strong> (edit expenses and profile anytime)</li>
                <li>• <strong>Erasure</strong> ("right to be forgotten" — delete account in Settings)</li>
                <li>• <strong>Object</strong> to analytics processing (opt-out in{' '}
                  <Button
                    variant="link"
                    className="h-auto p-0 text-primary text-sm inline"
                    onClick={() => navigate('/settings/privacy-analytics')}
                  >
                    Privacy & Analytics Settings
                  </Button>)
                </li>
                <li>• <strong>Data portability</strong> (contact us for structured export)</li>
                <li>• <strong>Lodge a complaint</strong> with your local data protection authority</li>
              </ul>
            </div>
          </div>

          <div className="pt-4 border-t text-sm text-muted-foreground">
            Last updated: January 2026
          </div>
        </Card>
      </div>
    </PageLayout>
  );
}